import { CardPrimitive } from '@figma/fpl-components';
import { borderRadius, color as fplColor } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@/UI/Grid/Grid';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { I18N_KEYS } from '@/language/keys';
import { DashboardCard } from './DashboardCard';
import { DashboardInlineEditInput } from './DashboardInlineEditInput';
import { FolderCardDisplay } from './components/FolderCardDisplay';
import { DashboardItem } from './DashboardMain';
import type { FolderResponseDto } from '@/types/folder.types';

const styles = stylex.create({
  root: {
    background: 'none',
    border: 'none',
    borderRadius: borderRadius.large,
    isolation: 'isolate',
    position: 'relative',
  },
  pointer: {
    cursor: 'pointer',
  },
  grabbing: {
    cursor: 'grabbing',
  },
  editing: {
    cursor: 'default',
  },
  mainButton: {
    background: 'none',
    borderRadius: 'inherit',
    outline: {
      default: 'none',
      ':focus-visible': `2px solid ${fplColor.borderSelected}`,
    },
    outlineOffset: '2px',
  },
});

interface FolderCardProps {
  folder: FolderResponseDto;
  onFolderNavigate?: (folderId?: string | null) => void;
  handleContextMenu?: (
    event: React.MouseEvent<HTMLDivElement>,
    id: string,
    itemType?: Extract<DashboardItem, 'folder'>,
  ) => void;
  workflowPosters?: string[];
  /** Thumbnail tile count, including subfolders. */
  workflowCount?: number;
  /** Subtitle count. Falls back to `workflowCount`, so the file label can still include subfolders. */
  labeledCount?: number;
  countKind?: 'file' | 'workflow';
  isEditing?: boolean;
  onRename?: (id: string, name: string) => void;
  onCancelRename?: (id: string) => void;
  isDragOver?: boolean;
  isDragging?: boolean;
  onDragOver?: (e: React.DragEvent, folderId: string) => void;
  onDragLeave?: () => void;
  onDrop?: (e: React.DragEvent, folderId: string) => void;
  onDragStart?: (e: React.DragEvent, itemId: string) => void;
  onDragEnd?: () => void;
}

export const FolderCard = ({
  folder,
  onFolderNavigate,
  handleContextMenu,
  workflowPosters = [],
  workflowCount = 0,
  labeledCount,
  countKind = 'file',
  isEditing = false,
  onRename,
  onCancelRename,
  isDragOver = false,
  isDragging = false,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
}: FolderCardProps) => {
  const [editingValue, setEditingValue] = useState(folder.name);
  const totalFiles = workflowCount || 0;
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setEditingValue(folder.name);
    }
  }, [isEditing, folder.name]);

  const handleClick = () => {
    if (onFolderNavigate && !isEditing) {
      onFolderNavigate(folder.id);
    }
  };

  const handleConfirmRename = useCallback(() => {
    onRename?.(folder.id, editingValue);
  }, [editingValue, folder.id, onRename]);

  const handleCancelRename = useCallback(() => {
    setEditingValue(folder.name);
    onCancelRename?.(folder.id);
  }, [folder.id, folder.name, onCancelRename]);

  const handleRightClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (handleContextMenu) {
      handleContextMenu(e, folder.id, 'folder');
    }
  };

  const inlineEdit = (
    <DashboardInlineEditInput
      isEditing={isEditing}
      value={editingValue}
      displayValue={folder.name}
      onChange={setEditingValue}
      onConfirm={handleConfirmRename}
      onCancel={handleCancelRename}
    />
  );

  if (isFplMigrationEnabled === true) {
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
        <CardPrimitive.Root
          data-testid={`folder-card-${folder.id}`}
          {...stylex.props(styles.root, isEditing ? styles.editing : isDragging ? styles.grabbing : styles.pointer)}
          draggable={!isEditing}
          onDragStart={(e) => onDragStart?.(e, folder.id)}
          onDragEnd={onDragEnd}
          onDragOver={(e) => {
            e.preventDefault();
            onDragOver?.(e, folder.id);
          }}
          onDragLeave={onDragLeave}
          onDrop={(e) => {
            e.preventDefault();
            onDrop?.(e, folder.id);
          }}
          onContextMenu={handleRightClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <CardPrimitive.MainButton
            {...stylex.props(styles.mainButton)}
            aria-label={t(I18N_KEYS.GENERAL.ARIA.OPEN_NAMED_ITEM, { name: folder.name })}
            onClick={handleClick}
          />
          <FolderCardDisplay
            id={folder.id}
            name={folder.name}
            posters={workflowPosters}
            totalFiles={totalFiles}
            fileCount={labeledCount}
            countKind={countKind}
            isHovered={isHovered}
            thumbnailSx={isDragOver ? { borderColor: color.White40_T } : undefined}
            nameContent={inlineEdit}
          />
        </CardPrimitive.Root>
      </Grid>
    );
  }

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }} sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
      <DashboardCard
        data-testid={`folder-card-${folder.id}`}
        draggable={!isEditing}
        onDragStart={(e) => onDragStart?.(e, folder.id)}
        onDragEnd={onDragEnd}
        onDragOver={(e) => {
          e.preventDefault();
          onDragOver?.(e, folder.id);
        }}
        onDragLeave={onDragLeave}
        onDrop={(e) => {
          e.preventDefault();
          onDrop?.(e, folder.id);
        }}
        sx={{
          cursor: isEditing ? 'default' : isDragging ? 'grabbing' : 'pointer',
          border: 'none',
        }}
        onClick={handleClick}
        onContextMenu={handleRightClick}
      >
        <FolderCardDisplay
          id={folder.id}
          name={folder.name}
          posters={workflowPosters}
          totalFiles={totalFiles}
          fileCount={labeledCount}
          countKind={countKind}
          thumbnailSx={isDragOver ? { borderColor: color.White40_T } : undefined}
          nameContent={
            <DashboardInlineEditInput
              isEditing={isEditing}
              value={editingValue}
              displayValue={folder.name}
              onChange={setEditingValue}
              onConfirm={handleConfirmRename}
              onCancel={handleCancelRename}
            />
          }
        />
      </DashboardCard>
    </Grid>
  );
};
